import React, { FC } from 'react';
import { create, StoreApi } from 'zustand';

/*
  The sequence store uses "blockers" to track whether to block the user from continuing to the next sequence.

  The default state is that there are no blockers (count is 0), which typically means the user will still need to wait for an audio or video
  playback to finish before they can click "Next".

  Items can add blockers, increasing the count. When there are blockers, the combo button will not render and the user
*/

export type ScormVersion = '1.2' | '2004';

export interface SequenceStore {
  sendRxdInteractions?: boolean;
  sendScormAgainInteractions?: boolean;
  scormVersion?: ScormVersion;
  autoContinue: boolean;
  personalization: boolean | undefined | null;
  notPlayNegativeFeedback: { [group: string]: boolean };
  setNotPlayNegativeFeedback: (id: string) => void;
  blockers: string[];
  addBlocker: (id: string, owner?: symbol) => void;
  removeBlocker: (id: string, owner?: symbol) => void;
  /** True while sequence content overflows and user hasn't scrolled to the bottom. */
  needsScrollDown: boolean;
  setNeedsScrollDown: (value: boolean) => void;
  audioPlayed: { [id: string]: boolean };
  setAudioPlayed: (id: string) => void;
  answers: { [group: string]: string[] };
  setAnswer: (group: string, id: string) => void;
  clearAnswer: (group: string, id: string) => void;
  clearBlocker: () => void;
  setAutoContinue: (value: boolean) => void;
  lastAnswer: string;
  setLastAnswer: (answer: string) => void;
  setPersonalization: (value: boolean) => void;
  setRxdInteractions: (value?: boolean) => void;
  setScormAgainInteractions: (value?: boolean) => void;
  setScormVersion: (value?: ScormVersion) => void;
  chatStarted: { [sequenceId: string]: boolean };
  setChatStarted: (sequenceId: string, started: boolean) => void;
  activeTabs: { [sequenceId: string]: number };
  getActiveTab: (sequenceId: string) => number;
  setActiveTab: (sequenceId: string, index: number) => void;
}

const createBlockerActions = (
  set: StoreApi<SequenceStore>['setState']
): Pick<SequenceStore, 'addBlocker' | 'removeBlocker' | 'clearBlocker'> => {
  const ownersById = new Map<string, Set<symbol | undefined>>();
  return {
    addBlocker: (id, owner) =>
      set(s => {
        const exists = s.blockers.includes(id);
        let owners = exists ? ownersById.get(id) : undefined;
        if (!owners) {
          // Existing untracked keys belong to legacy callers.
          owners = new Set(exists ? [undefined] : []);
          ownersById.set(id, owners);
        }
        owners.add(owner);
        return { blockers: [...new Set([...s.blockers, id])] };
      }),
    removeBlocker: (id, owner) =>
      set(s => {
        if (owner !== undefined) {
          if (!s.blockers.includes(id)) {
            ownersById.delete(id);
            return s;
          }
          const owners = ownersById.get(id);
          if (!owners?.delete(owner) || owners.size) return s;
        }
        // Omitting the owner retains the existing coalesced-key release.
        ownersById.delete(id);
        return { blockers: s.blockers.filter(b => b !== id) };
      }),
    clearBlocker: () => {
      ownersById.clear();
      set({ blockers: [], needsScrollDown: false });
    }
  };
};

const _useSequenceStore = create<SequenceStore>((set, get) => ({
  sendRxdInteractions: true,
  // TODO: drive from server (SequenceDetails) once backend exposes the flag.
  // Default `true` so a vanilla scorm-again launcher (no RXD wrapper) actually
  // reports back to its LMS. Detection inside `scormAgainAdapter` only flips
  // it on when `?scorm_version=…` is present in the iframe URL, so RXD-only
  // sessions stay quiet on the scorm-again side.
  sendScormAgainInteractions: true,
  // TODO: drive from server. Default to SCORM 2004 since modern packages
  // produced by our packaging service target 2004; override to '1.2' from
  // DevTools (`useSequenceStore.getState().setScormVersion('1.2')`) to test
  // legacy packages.
  scormVersion: '2004' as const,
  autoContinue: false,
  personalization: false,
  notPlayNegativeFeedback: {},
  setNotPlayNegativeFeedback: id =>
    set(s => ({
      notPlayNegativeFeedback: { ...s.notPlayNegativeFeedback, [id]: true }
    })),
  blockers: [],
  ...createBlockerActions(set),
  needsScrollDown: false,
  setNeedsScrollDown: value => set({ needsScrollDown: value }),
  audioPlayed: {},
  setAudioPlayed: id =>
    set(s => ({ audioPlayed: { ...s.audioPlayed, [id]: true } })),
  lastAnswer: 'default',
  setLastAnswer: answer => set({ lastAnswer: answer }),
  answers: {},
  setAnswer: (group, id) => {
    set(s => ({
      answers: {
        ...s.answers,
        [group]: [...new Set([...(s.answers?.[group] || []), id])]
      }
    }));
  },
  clearAnswer: (group, id) => {
    set(s => ({
      answers: {
        ...s.answers,
        [group]: (s.answers?.[group] || []).filter(a => a !== id)
      }
    }));
  },
  // setAutoContinue: value => set({ autoContinue: value }),
  setAutoContinue: value => set({ autoContinue: false }),
  setRxdInteractions: value => set({ sendRxdInteractions: value }),
  setScormAgainInteractions: value =>
    set({ sendScormAgainInteractions: value }),
  setScormVersion: value => set({ scormVersion: value }),
  setPersonalization: value => set({ personalization: value }),
  chatStarted: {},
  setChatStarted: (sequenceId, started) =>
    set(s => ({
      chatStarted: { ...s.chatStarted, [sequenceId]: started }
    })),
  activeTabs: {},
  getActiveTab: sequenceId => get().activeTabs[sequenceId] ?? 0,
  setActiveTab: (sequenceId, index) =>
    set(s => ({
      activeTabs: { ...s.activeTabs, [sequenceId]: index }
    }))
}));

export const SequenceProvider: FC = ({ children }) => <>{children}</>;

export const useSequenceStore = _useSequenceStore;
